namespace app.tarefas.minhasTarefas {
    "use strict";

    import AuthorizationService = app.support.authorization.AuthorizationService;
    import INotification = app.notification.INotification;
    import AuthService = app.autenticacao.AuthService;

    declare let _: _.LoDashStatic;
    
    export class TarefasCacheService {

        private static DELAY: number = 500;
        private static MAX_RETRIES: number = 3;

        private tasks: ITask[] = [];
        private loaded: boolean = false;
        private taskCreatedCallbackFn: Function;
        private taskAssignedCallbackFn: Function;
        private taskTaggedCallbackFn: Function;
        private taskCallbackFn: Function;
        
        /** @ngInject **/
        public constructor(private $state: ng.ui.IStateService, private authorizationService: AuthorizationService,
                    private authService: AuthService, private $timeout: ng.ITimeoutService, 
                    $rootScope: ng.IRootScopeService) {

            $rootScope.$on("$stateChangeSuccess", (event: ng.IAngularEvent, toState: ng.ui.IState) => {
                if (toState.name === "app.login") {
                    this.clear();
                }
            });
            
            $rootScope.$on("task:created", (event: ng.IAngularEvent, notification: INotification) => {
                this.onTaskCreated(notification);
            });
            
            $rootScope.$on("task:assigned", (event: ng.IAngularEvent, notification: INotification) => {
                this.onTaskAssigned(notification);
            });
            
            $rootScope.$on("task:tagged", (event: ng.IAngularEvent, notification: INotification) => {
                this.onTaskTagged(notification);
            });
            
            $rootScope.$on("task:renamed", (event: ng.IAngularEvent, notification: INotification) => {
                this.onTaskRenamed(notification);
            });            
            
            angular.forEach(["task:completed", "task:canceled", "task:removed", "task:hidden"], (removeEvent) => {
                $rootScope.$on(removeEvent, (event: ng.IAngularEvent, notification: INotification) => {
                    this.onTaskRemoved(notification);
                });                
            });
            
            angular.forEach({assigneeGroup: "task:assigned-group", priority: "task:prioritized",
                             notes: "task:annotated", draft: "task:saved", extraInfo: "task:informed",
                             restricted: "task:restricted"},
                            (eventName, property) => {
                $rootScope.$on(eventName, (event: ng.IAngularEvent, notification: INotification) => {
                    this.onTaskPropertyModified(notification, property);
                });
            });
        }
        
        public register(tasks: ITask[]): void {
            this.tasks.splice(0, this.tasks.length);
            this.tasks.push(...tasks);
            this.loaded = true;
        }
        
        public get(): ITask[] {
            return this.tasks;
        }
        
        public isLoaded(): boolean {
            return this.loaded;
        }
        
        public clear(): void {
            this.tasks.splice(0, this.tasks.length);
            this.loaded = false;
        }

        public remove(task: ITask): void {
            let tasksRemoved = _.remove(this.tasks, t => t.id === task.id && t.context === task.context);

            if (tasksRemoved.length > 0) {
                this.executeCallback(this.taskCallbackFn);
            }
        }        

        public registerTaskCreatedCallbackFn(cb: Function) {
            this.taskCreatedCallbackFn = cb;
        }

        public registerTaskAssignedCallbackFn(cb: Function) {
            this.taskAssignedCallbackFn = cb;
        }

        public registerTaskTaggedCallbackFn(cb: Function) {
            this.taskTaggedCallbackFn = cb;
        }

        public registerTaskCallbackFn(cb: Function) {
            this.taskCallbackFn = cb;
        }

        public unregisterCallbacks(): void {
            this.taskAssignedCallbackFn = undefined;
            this.taskCreatedCallbackFn = undefined;
            this.taskTaggedCallbackFn = undefined;
            this.taskCallbackFn = undefined;
        }

        private onTaskCreated(notification: INotification) {
            let task: ITask = notification.content;
            let taskFound = this.findTask(task.context, task.id);
            
            if (!taskFound) {
                this.authorizationService.findPermission(task.command, task.context, false)
                    .then(permission => this.loadCreatedTask(task, (!!permission ? false : true)))
                    .catch(() => this.loadCreatedTask(task, true));
                this.executeCallback(this.taskCreatedCallbackFn, task);
            }
        }

        private onTaskAssigned(notification: INotification, retries: number = 0): void {
            let task: ITask = notification.content;
            let taskFound = this.findTask(task.context, task.id);

            if (taskFound) {
                taskFound.assignee = task.assignee;
                taskFound.claimDate = task.claimDate;
                taskFound.assignor = task.assignor;
                taskFound.selfAssigned = taskFound.assignee === this.authService.getUserLogin();
                this.executeCallback(this.taskAssignedCallbackFn, taskFound);
            } else {
                this.retry((nextRetries) => this.onTaskAssigned(notification, nextRetries), retries);
            }
        }

        private onTaskTagged(notification: INotification, retries: number = 0): void {
            let task: ITask = notification.content;
            let taskFound = this.findTask(task.context, task.id);
                
            if (taskFound) {
                taskFound.etags = notification.content.tags;
                this.executeCallback(this.taskTaggedCallbackFn, taskFound);
            } else {
                this.retry((nextRetries) => this.onTaskTagged(notification, nextRetries), retries);
            }
        }

        private onTaskRenamed(notification: INotification, retries: number = 0): void {
            let task: ITask = notification.content;
            let taskFound = this.findTask(task.context, task.id);
            
            if (taskFound) {
                taskFound.title = task.title;
                
                if (!!task.searchableId && task.searchableId.length > 0) {
                    taskFound.searchableId = task.searchableId;
                }

                if (!!task.informationId && task.informationId.length > 0) {
                    taskFound.informationId = task.informationId;
                }
                this.executeCallback(this.taskCallbackFn, taskFound);
            } else {
                this.retry((nextRetries) => this.onTaskRenamed(notification, nextRetries), retries);
            }
        }

        private onTaskRemoved(notification: INotification, retries: number = 0): void {
            let task: ITask = notification.content;
            let tasksRemoved = _.remove(this.tasks, t => t.id === task.id && t.context === task.context);

            if (tasksRemoved.length > 0) {
                this.executeCallback(this.taskCallbackFn);
            } else {
                this.retry((nextRetries) => this.onTaskRemoved(notification, nextRetries), retries);
            }
        }
        
        private onTaskPropertyModified(notification: INotification, property: string, retries: number = 0): void {
            let task: ITask = notification.content;
            let taskFound = this.findTask(task.context, task.id);
            
            if (taskFound) {
                taskFound[property] = task[property];
                this.executeCallback(this.taskCallbackFn, taskFound);
            } else {
                this.retry((nextRetries) => this.onTaskPropertyModified(notification, property, nextRetries), retries);
            }
        }

        private loadCreatedTask(task: ITask, readOnly: boolean): void {
            task.readOnly = readOnly;
            task.startDate = new Date(task.startDate).toISOString();
            this.tasks.push(task);
        }
        
        private findTask(context: string, id: string): ITask {
            return this.tasks.find((task) => task.id === id && task.context === context);
        }

        private executeCallback(cb: Function, task?: ITask): void {
            if (!!cb && this.isTaskState()) {
                cb(task);
            }
        }

        private retry(eventFn: Function, retries: number): void {
            if (retries <= TarefasCacheService.MAX_RETRIES) {
                this.$timeout(() => {
                    retries++;
                    eventFn(retries);
                }, TarefasCacheService.DELAY);
            }
        }

        private isTaskState(): boolean {
            return this.$state.current.name === "app.tarefas.minhas-tarefas";
        }        
    }
    
    const SERVICE_NAME: string = "app.tarefas.minhas-tarefas.TarefasCacheService"; 
    
    angular
        .module("app.tarefas.minhas-tarefas")
        .service(SERVICE_NAME, TarefasCacheService);
}
